import { Network } from '@capacitor/network';
import { Capacitor } from '@capacitor/core';
import OfflineLogger from '@/utils/offlineLogger';
import { startConnectivityChecks } from './connectivityChecks';

const PLUGIN_MISSING_WARNING =
  'Capacitor Network plugin not available, using navigator.onLine fallback';
const PLUGIN_ERROR_WARNING =
  'Capacitor Network plugin error, using navigator.onLine fallback';

function initializeWithNavigatorFallback(
  updateStatus: (online: boolean) => void,
  isTest: boolean,
  warnMessage: string
): boolean {
  if (warnMessage) {
    OfflineLogger.warn('NetworkService', warnMessage);
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => updateStatus(true));
    window.addEventListener('offline', () => updateStatus(false));
    const online = navigator.onLine;
    OfflineLogger.info(
      'NetworkService',
      `Initial browser network status: ${online ? 'online' : 'offline'}`
    );
    if (!isTest) {
      startConnectivityChecks(updateStatus);
    }
    return online;
  }

  OfflineLogger.info('NetworkService', 'Window is undefined, assuming online');
  return true;
}

/**
 * Imposta i listener di stato rete e avvia i controlli di connettività.
 * @param updateStatus funzione per aggiornare lo stato di rete
 * @param isTest indica se siamo in ambiente di test
 * @returns stato iniziale della connessione
 */
export async function initializeNetwork(
  updateStatus: (online: boolean) => void,
  isTest: boolean
): Promise<boolean> {
  OfflineLogger.info('NetworkService', 'Initializing');

  if (Capacitor.isPluginAvailable('Network')) {
    try {
      Network.addListener('networkStatusChange', status => updateStatus(status.connected));
      const initial = await Network.getStatus();
      OfflineLogger.info(
        'NetworkService',
        `Initial network status: ${initial.connected ? 'online' : 'offline'}`
      );
      // Avvia anche i controlli periodici verso Supabase: il plugin Capacitor
      // notifica solo i cambi di stato del SO, quindi senza questo ping lo stato
      // non potrebbe recuperare da un eventuale falso "offline".
      if (!isTest) {
        startConnectivityChecks(updateStatus);
      }
      return initial.connected;
    } catch (error) {
      OfflineLogger.error('NetworkService.initializeNetwork', error);
      return initializeWithNavigatorFallback(updateStatus, isTest, PLUGIN_ERROR_WARNING);
    }
  }

  return initializeWithNavigatorFallback(updateStatus, isTest, PLUGIN_MISSING_WARNING);
}
